/**
 * WorkflowPermissionsPanel — right-side drawer opened from the Users list.
 *
 * Two stacked sections:
 *   1) 19-checkbox grid (grouped visually) + "Reset to role defaults"
 *      + "Copy permissions from…" (destructive confirm dialog).
 *   2) Mapped Distributors: multi-select picker (filtered by user's zones)
 *      + a mappings table with per-row remove.
 *
 * All writes route through the Section-C audit history system automatically.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ShieldCheck, Users as UsersIcon, RefreshCw, Copy, Trash2, Loader2, X, Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";


export default function WorkflowPermissionsPanel({
  open, onOpenChange, targetUser,
}) {
  const [catalog, setCatalog] = useState(null);   // {items, groups, order}
  const [perms, setPerms] = useState({ permission_codes: [], role_defaults: [], role_code: null });
  const [busy, setBusy] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);

  // Distributor mapping
  const [mappings, setMappings] = useState([]);
  const [availDistributors, setAvailDistributors] = useState([]);
  const [distSearch, setDistSearch] = useState("");

  const loadAll = useCallback(async () => {
    if (!targetUser?.id) return;
    setBusy(true);
    try {
      const [cat, up, m] = await Promise.all([
        api.get("/workflow-permissions"),
        api.get(`/users/${targetUser.id}/workflow-permissions`),
        api.get(`/user-distributor-map/users/${targetUser.id}/distributors`, {
          params: { page: 1, page_size: 200 },
        }),
      ]);
      setCatalog(cat.data);
      setPerms(up.data);
      setMappings(m.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load permissions");
    } finally {
      setBusy(false);
    }
  }, [targetUser?.id]);

  useEffect(() => { if (open) loadAll(); }, [open, loadAll]);

  // Load selectable distributors from user's zones. Admin/mgmt/mis can see
  // everything; here we use scope=all to circumvent the mapping filter (admin
  // caller) so we can pick fresh distributors to map.
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/distributors", {
          params: { page: 1, page_size: 2000, scope: "all",
                    q: distSearch || undefined, status: "active" },
        });
        setAvailDistributors(data.items || []);
      } catch (e) {
        // ignore
      }
    })();
  }, [open, distSearch]);

  const permsSet = useMemo(
    () => new Set(perms.permission_codes || []),
    [perms]
  );

  const togglePerm = useCallback((code) => {
    setPerms((prev) => {
      const s = new Set(prev.permission_codes || []);
      if (s.has(code)) s.delete(code); else s.add(code);
      return { ...prev, permission_codes: Array.from(s).sort() };
    });
  }, []);

  const savePerms = useCallback(async () => {
    setBusy(true);
    try {
      await api.put(`/users/${targetUser.id}/workflow-permissions`, {
        permission_codes: perms.permission_codes || [],
      });
      toast.success("Permissions saved");
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  }, [targetUser?.id, perms.permission_codes, loadAll]);

  const resetDefaults = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/users/${targetUser.id}/workflow-permissions/reset`
      );
      setPerms((p) => ({ ...p, permission_codes: data.permission_codes || [] }));
      toast.success(`Reset to ${perms.role_code} defaults`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  }, [targetUser?.id, perms.role_code]);

  const mappedIds = useMemo(
    () => new Set(mappings.map((m) => m.distributor_id)),
    [mappings]
  );

  const addMapping = useCallback(async (dist) => {
    try {
      await api.post("/user-distributor-map", {
        user_id: targetUser.id, distributor_id: dist.id,
      });
      toast.success(`Mapped ${dist.code}`);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mapping failed");
    }
  }, [targetUser?.id, loadAll]);

  const removeMapping = useCallback(async (m) => {
    try {
      await api.delete("/user-distributor-map", {
        params: { user_id: m.user_id, distributor_id: m.distributor_id },
      });
      toast.success(`Removed ${m.distributor_code}`);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    }
  }, [loadAll]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl overflow-y-auto"
        data-testid="workflow-perms-panel"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-[#6D2C91]" />
            Workflow Permissions & Distributor Mapping
          </SheetTitle>
          <SheetDescription>
            {targetUser?.name || "—"}
            {targetUser?.role_code && (
              <>
                {" "}·{" "}
                <Badge className="bg-neutral-100 text-neutral-800 hover:bg-neutral-100">
                  {targetUser.role_code}
                </Badge>
              </>
            )}
          </SheetDescription>
        </SheetHeader>

        {/* ---------- Permissions Grid ---------- */}
        <section className="mt-5" data-testid="wf-perm-section">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="text-sm font-semibold text-neutral-800">Feature permissions</div>
            <div className="flex items-center gap-1.5">
              <Button
                size="sm" variant="outline"
                onClick={resetDefaults} disabled={busy}
                data-testid="wf-reset-defaults"
              >
                <RefreshCw className="h-3.5 w-3.5 mr-1" />
                Reset to role defaults
              </Button>
              <Button
                size="sm" variant="outline"
                onClick={() => setCopyOpen(true)} disabled={busy}
                data-testid="wf-copy-from"
              >
                <Copy className="h-3.5 w-3.5 mr-1" />
                Copy permissions from…
              </Button>
            </div>
          </div>

          {busy && !catalog ? (
            <div className="text-center py-8 text-neutral-400">
              <Loader2 className="h-5 w-5 animate-spin inline-block" />
            </div>
          ) : (
            <PermissionsGrid
              catalog={catalog}
              permsSet={permsSet}
              onToggle={togglePerm}
            />
          )}

          <div className="mt-3 flex items-center justify-between">
            <div className="text-[11px] text-neutral-500">
              {perms.matches_defaults
                ? "Matches role defaults."
                : `Override — ${perms.extra_granted?.length || 0} extra granted, ${perms.extra_denied?.length || 0} denied.`}
            </div>
            <Button size="sm" onClick={savePerms} disabled={busy}
                    className="bg-[#6D2C91] hover:bg-[#5a2378] text-white"
                    data-testid="wf-save-perms">
              Save permissions
            </Button>
          </div>
        </section>

        {/* ---------- Mapped Distributors ---------- */}
        <section className="mt-8" data-testid="wf-dist-section">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="text-sm font-semibold text-neutral-800 flex items-center gap-1">
              <UsersIcon className="h-4 w-4" /> Mapped distributors
              <Badge className="bg-neutral-100 text-neutral-700 hover:bg-neutral-100 ml-1"
                     data-testid="wf-map-count">
                {mappings.length} selected
              </Badge>
            </div>
          </div>

          {/* Selected chips */}
          <div className="flex flex-wrap gap-1.5 min-h-[2rem]" data-testid="wf-map-chips">
            {mappings.length === 0 && (
              <div className="text-xs text-neutral-400 italic">
                No distributors mapped yet.
              </div>
            )}
            {mappings.map((m) => (
              <span key={m.map_id}
                    data-testid={`wf-map-chip-${m.distributor_code}`}
                    className="inline-flex items-center gap-1 rounded-full bg-[#F3E7FA] text-[#6D2C91] px-2 py-0.5 text-xs">
                {m.distributor_code}
                <button type="button" onClick={() => removeMapping(m)}
                        className="hover:text-red-600" aria-label={`remove ${m.distributor_code}`}>
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>

          {/* Picker */}
          <div className="mt-3">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
              <Input
                placeholder="Search distributors by code / name / phone…"
                className="pl-8 h-9"
                value={distSearch}
                onChange={(e) => setDistSearch(e.target.value)}
                data-testid="wf-dist-search"
              />
            </div>
            <div className="mt-2 max-h-52 overflow-y-auto rounded-lg border border-neutral-200 divide-y">
              {availDistributors
                .filter((d) => !mappedIds.has(d.id))
                .slice(0, 40)
                .map((d) => (
                  <button
                    key={d.id} type="button"
                    onClick={() => addMapping(d)}
                    className="w-full text-left px-3 py-1.5 hover:bg-neutral-50 flex items-center justify-between text-xs"
                    data-testid={`wf-dist-option-${d.code}`}
                  >
                    <span>
                      <span className="font-medium text-neutral-800">{d.code}</span>
                      <span className="text-neutral-500"> · {d.name}</span>
                    </span>
                    <span className="text-neutral-400 text-[10px]">Click to add</span>
                  </button>
                ))}
              {availDistributors.filter((d) => !mappedIds.has(d.id)).length === 0 && (
                <div className="text-center text-neutral-400 text-xs py-4">
                  All available distributors already mapped.
                </div>
              )}
            </div>
          </div>

          {/* Mappings table */}
          {mappings.length > 0 && (
            <div className="mt-3 overflow-x-auto rounded-lg border border-neutral-200"
                 data-testid="wf-map-table">
              <table className="w-full text-xs">
                <thead className="bg-neutral-50 text-neutral-500">
                  <tr>
                    <th className="text-left p-2">Code</th>
                    <th className="text-left p-2">Name</th>
                    <th className="text-left p-2">Zone</th>
                    <th className="text-left p-2">Added</th>
                    <th className="text-right p-2 w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {mappings.map((m) => (
                    <tr key={m.map_id} data-testid={`wf-map-row-${m.distributor_code}`}>
                      <td className="p-2 font-medium">{m.distributor_code}</td>
                      <td className="p-2">{m.distributor_name}</td>
                      <td className="p-2 text-neutral-500">{m.distributor_zone_id?.slice(0, 8) || "—"}</td>
                      <td className="p-2 text-neutral-500">{
                        m.created_at ? new Date(m.created_at).toLocaleDateString() : "—"
                      }</td>
                      <td className="p-2 text-right">
                        <button onClick={() => removeMapping(m)}
                                className="text-red-600 hover:text-red-700"
                                aria-label={`remove ${m.distributor_code}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </SheetContent>

      <CopyFromDialog
        open={copyOpen} onOpenChange={setCopyOpen}
        targetUser={targetUser}
        onCopied={loadAll}
      />
    </Sheet>
  );
}


// ---------- Permission grid ----------
function PermissionsGrid({ catalog, permsSet, onToggle }) {
  if (!catalog) return null;
  const byGroup = {};
  for (const p of catalog.items || []) {
    const g = p.group_key || "other";
    (byGroup[g] = byGroup[g] || []).push(p);
  }
  const orderedGroups = ["sales", "outlet", "field_ops", "primary", "admin"];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="wf-perm-grid">
      {orderedGroups
        .filter((g) => (byGroup[g] || []).length > 0)
        .map((g) => (
          <div key={g}
               className="rounded-xl border border-neutral-200 p-3 bg-white"
               data-testid={`wf-group-${g}`}>
            <div className="text-[11px] uppercase tracking-widest text-[#6D2C91] font-semibold mb-1.5">
              {catalog.groups[g] || g}
            </div>
            <div className="space-y-1.5">
              {byGroup[g].map((p) => (
                <label key={p.code}
                       className="flex items-start gap-2 text-xs cursor-pointer"
                       data-testid={`wf-perm-row-${p.code}`}>
                  <Checkbox
                    checked={permsSet.has(p.code)}
                    onCheckedChange={() => onToggle(p.code)}
                    data-testid={`wf-perm-checkbox-${p.code}`}
                  />
                  <span className="flex-1">
                    <span className="font-medium text-neutral-800">{p.name}</span>
                    <span className="ml-1 text-[10px] font-mono text-neutral-400">{p.code}</span>
                    {p.description && (
                      <span className="block text-[10px] text-neutral-500 leading-tight">
                        {p.description}
                      </span>
                    )}
                  </span>
                </label>
              ))}
            </div>
          </div>
        ))}
    </div>
  );
}


// ---------- Copy From dialog (destructive confirm) ----------
function CopyFromDialog({ open, onOpenChange, targetUser, onCopied }) {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setSelected(null); setQ(""); return; }
    (async () => {
      try {
        const { data } = await api.get("/users", {
          params: { page: 1, page_size: 30, q: q || undefined },
        });
        setUsers((data.items || []).filter((u) => u.id !== targetUser?.id));
      } catch (e) { /* ignore */ }
    })();
  }, [open, q, targetUser?.id]);

  const doCopy = useCallback(async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.post(`/users/${targetUser.id}/workflow-permissions/copy-from`, {
        source_user_id: selected.id,
      });
      toast.success(`Copied permissions from ${selected.name}`);
      onOpenChange(false);
      onCopied?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Copy failed");
    } finally { setBusy(false); }
  }, [selected, targetUser?.id, onCopied, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="wf-copy-dialog">
        <DialogHeader>
          <DialogTitle>Copy permissions from another user</DialogTitle>
          <DialogDescription asChild>
            <div>
              This will <span className="font-semibold text-red-600">replace</span> {" "}
              <span className="font-semibold">{targetUser?.name}</span>'s current
              permission set with the source user's set. This cannot be auto-undone.
            </div>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Input
            placeholder="Search source user…"
            value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="wf-copy-search"
          />
          <div className="max-h-56 overflow-y-auto rounded border border-neutral-200 divide-y">
            {users.map((u) => (
              <button
                key={u.id} type="button"
                onClick={() => setSelected(u)}
                data-testid={`wf-copy-option-${u.id}`}
                className={
                  "w-full text-left px-2 py-1.5 text-xs hover:bg-neutral-50 " +
                  (selected?.id === u.id ? "bg-[#F3E7FA] text-[#6D2C91] font-medium" : "")
                }
              >
                {u.name} <span className="text-neutral-400">· {u.role_code}</span>{" "}
                {u.email && <span className="text-neutral-400">· {u.email}</span>}
              </button>
            ))}
            {users.length === 0 && (
              <div className="text-center text-neutral-400 text-xs py-4">
                No users found.
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={doCopy} disabled={!selected || busy}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="wf-copy-confirm">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> :
              (selected ? `Copy from ${selected.name}` : "Select a user")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
